import { DeleteOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React from "react";

import { formatAmount } from "@/components/forecasting/forecastTotals";

export interface ICostColumn {
  key: string;
  header: React.ReactNode;
  /** Width of the column, e.g. `w-[280px]` or `flex-1`. Shared by header and cells. */
  className: string;
}

export interface ICostRow {
  key: string | number;
  cells: Record<string, React.ReactNode>;
  /** Omit when a cell already shows the amount as an input. */
  amount?: number;
  removeLabel: string;
  onRemove: () => void;
}

const AMOUNT_CLASS = "w-[120px] pl-4";
const ACTIONS_CLASS = "w-[48px] text-center";

/**
 * Single-row-per-entry table shared by the cost sections. Amount and remove
 * columns are appended to every table so they line up across sections.
 */
export const CostTable = ({
  columns,
  rows,
  showAmount = true,
  rowPadding = "py-3"
}: {
  columns: ICostColumn[];
  rows: ICostRow[];
  showAmount?: boolean;
  /** Vertical padding per row — roomier for rows with stacked fields. */
  rowPadding?: string;
}) => (
  <div className="overflow-x-auto">
    <div className="min-w-[1000px]">
      <div className="px-4 py-2 border-0 border-b border-solid border-gray-200 bg-gray-50 flex gap-4 items-end text-xs font-medium text-gray-500 uppercase tracking-wider">
        {columns.map((column) => (
          <div key={column.key} className={column.className}>
            {column.header}
          </div>
        ))}
        {showAmount && <div className={AMOUNT_CLASS}>Amount</div>}
        <div className={ACTIONS_CLASS}>
          <span className="sr-only">Actions</span>
        </div>
      </div>

      {/* Preflight is off in this app, so borders need an explicit style. */}
      <div>
        {rows.map((row) => (
          <div
            key={row.key}
            className={`px-4 ${rowPadding} flex gap-4 items-center border-0 border-t border-solid border-gray-200 first:border-t-0 even:bg-gray-50 hover:bg-blue-50/40`}
          >
            {columns.map((column) => (
              <div key={column.key} className={column.className}>
                {row.cells[column.key]}
              </div>
            ))}
            {showAmount && (
              <div
                className={`${AMOUNT_CLASS} font-semibold text-gray-900 tabular-nums`}
              >
                £{formatAmount(row.amount ?? 0)}
              </div>
            )}
            <div className={ACTIONS_CLASS}>
              <Button
                type="text"
                danger
                aria-label={row.removeLabel}
                icon={<DeleteOutlined />}
                onClick={row.onRemove}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/** Dashed box that marks fields belonging together. */
export const CostOutline = ({ children }: { children: React.ReactNode }) => (
  <div className="rounded-lg border-2 border-dashed border-gray-200 bg-white p-3">
    {children}
  </div>
);

/** Columns laid out side by side inside one table cell, so related figures read as a group. */
export const CostGroup = ({ children }: { children: React.ReactNode }) => (
  <div className="grid grid-cols-4 gap-3 items-center">{children}</div>
);

/** A value the user can't edit, shown as text rather than a disabled input. */
export const CostValue = ({ children }: { children: React.ReactNode }) => (
  <span className="text-gray-700 tabular-nums">{children}</span>
);
